import React, { useEffect, useState } from 'react';
import MuiIconButton from '@mui/material/IconButton';
import IconButton from 'pencil/IconButton';

import Menu from '@mui/material/Menu';
import Badge from '@mui/material/Badge';
import BellOutlineIcon from 'mdi-react/BellOutlineIcon';
import TrashIcon from 'mdi-react/TrashIcon';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from 'pencil/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { makeStyles } from 'pencil/styles';
import * as I18n from 'circe/I18n';
import {
  getNotifications,
  markNotificationAsRead,
  countUnreadNotifications,
} from 'domain/layout/utils';
import { getNotificationIcon } from 'domain/layout/model';
import EmptyIcon from 'assets/img/empty.svg';
import SVG from 'react-inlinesvg';
import moment from 'moment';
import { NOTIFICATIONS } from 'domain/layout/constants';

const useStyles = makeStyles((theme) => ({
  notificationsMenu: {
    marginTop: '60px',
    '& .MuiMenu-paper': {
      padding: '15px 20px',
      borderRadius: '16px',
      width: '430px',
    },
    '& .MuiMenu-paper::-webkit-scrollbar': {
      width: '8px',
    },
    '& .MuiMenu-paper::-webkit-scrollbar-track': {
      background: '#adaaaa33',
      borderRadius: '8px',
    },
    '& .MuiMenu-paper::-webkit-scrollbar-thumb': {
      background: '#afafaf8c',
      borderRadius: '8px',
    },
  },
  singleNotification: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '15px',
    padding: '10px 0',
    paddingRight: '20px',
    borderBottom: '1px #E0E0E0 solid',
    cursor: 'pointer',
  },
  notificationsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    margin: '20px 0',
  },
  notificationContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  emptyNotifications: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '90%',
    margin: '30px auto',
    textAlign: 'center',
  },
  notificationIcon: {
    color: '#000000',
  },
  containerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  deleteAllIconButton: {
    background: theme.palette.primary.main,
  },
}));

const SingleNotification = ({ notification, setNotifications }) => {
  const classes = useStyles();

  const updateNotifications = (id) => {
    const updatedNotifications = markNotificationAsRead(id);
    setNotifications(updatedNotifications);
  };

  return (
    <Box
      className={classes.singleNotification}
      key={notification.id}
      onClick={() => updateNotifications(notification.id)}
    >
      <Box sx={{ display: 'flex', alignItems: 'start', gap: '15px' }}>
        {getNotificationIcon(notification)}

        <Box className={classes.notificationContent}>
          {notification?.notification?.title && (
            <Typography
              sx={{ fontWeight: '600' }}
              variant="body1"
              component="body1"
              color="text.primary"
            >
              {notification?.notification?.title}
            </Typography>
          )}

          {notification?.notification?.body && (
            <Typography
              sx={{ fontWeight: '400', fontSize: '14px' }}
              variant="body2"
              component="body2"
              color="text.primary"
            >
              {notification?.notification?.body}
            </Typography>
          )}

          {notification?.data?.url && (
            <Typography
              sx={{ fontWeight: '400', fontSize: '14px' }}
              variant="body2"
              component="body2"
              color="text.secondary"
            >
              <a href={notification?.data?.url} target="_blank">
                {I18n.str('clickHereToDownload', 'Click here to download')}
              </a>
            </Typography>
          )}

          {notification?.createdAt && (
            <Typography
              sx={{ fontWeight: '300', fontSize: '11px' }}
              variant="body2"
              component="body2"
              color="text.secondary"
            >
              {moment(notification?.createdAt).format('MMM Do YYYY, h:mm a')}
            </Typography>
          )}
        </Box>
      </Box>
      {notification.status === 'unread' && <Badge color="error" variant="dot" />}
    </Box>
  );
};

const NotificationContainer = ({
  newNotifications,
  clearNotifications,
  prevNewNotificationsLength,
}) => {
  const [notifications, setNotifications] = useState(getNotifications());

  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = () => {
    localStorage.removeItem(NOTIFICATIONS.key);
    setNotifications([]);
    clearNotifications();
    prevNewNotificationsLength.current = 0;
    handleCloseDeleteDialog();
  };

  useEffect(() => {
    setNotifications(getNotifications());
  }, [newNotifications]);

  return (
    <div>
      <MuiIconButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpenMenu}
      >
        <Badge badgeContent={countUnreadNotifications()} color="error" max={9}>
          <BellOutlineIcon className={classes.notificationIcon} />
        </Badge>
      </MuiIconButton>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        className={classes.notificationsMenu}
      >
        <Box className={classes.containerHeader}>
          <Typography sx={{ fontWeight: '600' }} variant="h5" component="h5" color="text.primary">
            {I18n.str('notifications', 'Notifications')}
          </Typography>
          {notifications.length ? (
            <>
              <IconButton
                variant="contained"
                onClick={handleOpenDeleteDialog}
                className={classes.deleteAllIconButton}
                title={I18n.str('deleteAll', 'Delete All')}
              >
                <TrashIcon />
              </IconButton>
              <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">
                  {I18n.str(
                    'confirmDeleteAllNotifications',
                    'Are you sure you want to delete all notifications?',
                  )}
                </DialogTitle>
                <DialogActions>
                  <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                  <Button
                    onClick={handleConfirmDelete}
                    color="primary"
                    variant="contained"
                    autoFocus
                  >
                    {I18n.str('confirmDelete', 'Confirm Delete')}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <></>
          )}
        </Box>

        <Box className={classes.notificationsContainer}>
          {notifications.length ? (
            notifications.map((e) => (
              <SingleNotification notification={e} setNotifications={setNotifications} />
            ))
          ) : (
            <Box className={classes.emptyNotifications}>
              <SVG src={EmptyIcon} title="Empty" />

              <Typography
                sx={{ fontSize: '14px' }}
                variant="body2"
                component="body2"
                color="text.secondary"
              >
                {I18n.str('emptyNotifications', 'No notifications to display')}
              </Typography>
            </Box>
          )}
        </Box>
      </Menu>
    </div>
  );
};

export default NotificationContainer;
